import { Global, css } from '@emotion/react'
import vndlyColors, { brand as vndlyBrand } from '~/theme/colors'

/**
 * Frozen snapshot of the pre-Canvas-v4 CSS variable values.
 * Rendered when the sana_rebrand_enabled feature flag is OFF.
 *
 * All canvas token references have been resolved to their v3 CSS variable name
 * strings so this component is immune to canvas-tokens-web package upgrades.
 */
export default function LegacyGlobalCSSVariables() {
  return (
    <Global
      styles={css`
        /**
         * ============================================================================
         * CANVAS TOKEN POLYFILLS — legacy mode only
         * ============================================================================
         *
         * Canvas's own CSS is injected globally by webpack. Emotion's <Global>
         * mounts after that, so anything declared here wins via cascade order.
         *
         * Two layers, applied in order. Later blocks override earlier ones for
         * any token they both touch — the trailing block reflects the more
         * specific intent.
         *
         * When the \`sana_rebrand_enabled\` flag is retired, this entire file
         * (polyfills + legacy vars) goes away.
         */

        /**
         * Layer 1: v3 token values
         *
         * Canvas v4 shifted some system-token values vs v3. Restore the v3
         * values so components that haven't migrated still look like they did
         * before the canvas-tokens-web upgrade.
         */
        :root {
          --cnvs-v3-overrides: 'true';
          --cnvs-sys-shape-xxl: 0.375rem;
          /* Legacy card border / divider colors (was Chakra gray.300 = #dddddd) */
          --cnvs-sys-color-border-strong: ${vndlyColors.gray[300]};
          --cnvs-sys-color-border-default: ${vndlyColors.gray[300]};

          /**
           * Cascading override, not a theme.isSanaRebrand ternary. ItemsList edge
           * slots read this. Parents (ResultsLayout, ResultItem grid) set 0 / unset /
           * xxl per layout. Flag-ON leaves it unset so rows inherit the card radius.
           */
          --vndly-legacy-items-list-edge-radius: 0;

          --cnvs-sys-color-bg-alt-default: var(--cnvs-base-palette-slate-100);
          --cnvs-sys-color-border-input-default: var(--cnvs-base-palette-slate-500);
          --cnvs-sys-color-fg-default: var(--cnvs-base-palette-neutral-800);
          --cnvs-sys-color-fg-muted-default: var(--cnvs-base-palette-slate-600);
          --cnvs-sys-color-fg-muted-strong: var(--cnvs-base-palette-slate-700);
          --cnvs-sys-color-fg-strong: var(--cnvs-base-palette-neutral-900);
          --cnvs-sys-color-fg-stronger: var(--cnvs-base-palette-neutral-950);
          --cnvs-sys-color-fg-disabled: var(--cnvs-base-palette-slate-400);
          --cnvs-sys-color-fg-contrast-default: var(--cnvs-base-palette-neutral-900);
          --cnvs-sys-color-fg-contrast-strong: var(--cnvs-base-palette-neutral-950);
          --cnvs-sys-color-border-contrast-default: var(--cnvs-base-palette-neutral-900);

          /* Canvas semantics with globally valid legacy equivalents. */
          --cnvs-sys-color-brand-accent-primary: var(--cnvs-brand-primary-base);
          --cnvs-sys-color-brand-accent-critical: var(--cnvs-brand-error-base);
          --cnvs-sys-color-brand-fg-primary-default: var(--cnvs-brand-primary-base);
          --cnvs-sys-color-brand-fg-primary-strong: var(--cnvs-brand-primary-dark);
          --cnvs-sys-color-brand-fg-critical-default: var(--cnvs-brand-error-base);
          --cnvs-sys-color-surface-transparent: var(--cnvs-sys-color-bg-transparent-default);
          --cnvs-sys-font-family-default: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif;
          --cnvs-sys-letter-spacing-subtext-lg: normal;

          /* Button-specific values that cannot safely redefine a shared Canvas
             primitive or do not map to a single Canvas token. */
          --vndly-button-border-radius: 0.25rem;
          --vndly-button-font-weight: 500;
          --vndly-button-small-line-height: 1.5;
          --vndly-button-medium-line-height: 1.5;
          --vndly-button-medium-min-width: 2.5rem;
          --vndly-button-medium-padding-inline: 1rem;
          --vndly-button-tertiary-medium-min-width: 2.5rem;
          --vndly-button-tertiary-medium-padding-inline: 1rem;
          --vndly-button-spinner-color: var(--cnvs-brand-primary-base);
          --vndly-button-state-background-image: none;
          --vndly-button-focus-ring-middle: inset 0 0 0 2px white, 0 0 0 2px var(--cnvs-brand-common-focus-outline);
          --vndly-button-focus-ring-outer: 0 0 0 2px white, 0 0 0 4px var(--cnvs-brand-common-focus-outline);
          --vndly-button-primary-focus-background: var(--cnvs-brand-common-focus-outline);
          --vndly-button-background-primary-hover: var(--cnvs-brand-primary-dark);
          --vndly-button-background-primary-active: var(--cnvs-brand-primary-darkest);
          --vndly-button-background-secondary: var(--cnvs-sys-color-bg-default);
          --vndly-button-secondary: var(--cnvs-sys-color-fg-strong);
          --vndly-button-background-secondary-hover: var(--cnvs-sys-color-bg-alt-default);
          --vndly-button-border-secondary-hover: var(--cnvs-sys-color-border-input-strong);
          --vndly-button-secondary-hover: var(--cnvs-sys-color-fg-stronger);
          --vndly-button-background-secondary-active: var(--cnvs-sys-color-bg-alt-strong);
          --vndly-button-background-delete-primary-hover: var(--cnvs-brand-error-dark);
          --vndly-button-background-delete-primary-active: var(--cnvs-brand-error-darkest);
          --vndly-button-background-delete-secondary: var(--cnvs-sys-color-bg-default);
          --vndly-button-border-delete-secondary: var(--cnvs-sys-color-border-contrast-strong);
          --vndly-button-background-delete-secondary-hover: var(--cnvs-brand-error-dark);
          --vndly-button-border-delete-secondary-hover: var(--cnvs-brand-error-dark);
          --vndly-button-background-delete-secondary-active: var(--cnvs-brand-error-darkest);
          --vndly-button-tertiary-background-hover: var(--cnvs-sys-color-bg-alt-soft);
          --vndly-button-tertiary-focus-color: var(--cnvs-brand-common-focus-outline);
          --vndly-button-tertiary-background-active: var(--cnvs-sys-color-bg-alt-default);

          --vndly-button-quick-action-color: ${vndlyColors.gray[800]};
          --vndly-button-quick-action-background: ${vndlyColors.gray[50]};
          --vndly-button-quick-action-border: ${vndlyColors.gray[300]};
          --vndly-button-quick-action-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);

          /* QuickAction's legacy md/lg shadows are depth outliers, so local variables
             preserve them without changing the canonical Canvas depth scale. */
          --vndly-quick-action-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          --vndly-quick-action-hover-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);

          /**
           * B02 form slice — restore v3 input values for the non-deprecated Canvas
           * tokens the six form theme files now reference directly. Each maps the v4
           * token back to the value it resolved to before the form migration.
           * (Default border slate-500, fg-default, fg-muted-default and fg-disabled are
           * already restored above and cover field text / placeholder / helper / label /
           * disabled; disabled bg and addon border are pinned to base palette directly.)
           */
          /* Inputs render shape.md (8px) in flag-ON; legacy inputs were 0.25rem. lg goes
             0.375rem → 0.25rem — the one accepted outlier per the canonical-legacy rule. */
          --cnvs-sys-shape-md: 0.25rem;
          /* Hover border was the (now-deprecated) input.strong = slate-700. */
          --cnvs-sys-color-border-input-hover: var(--cnvs-sys-color-border-input-strong);
          /* Invalid border was the (now-deprecated) border.critical.default = red-500. */
          --cnvs-sys-color-brand-border-critical: var(--cnvs-sys-color-border-critical-default);
          /* Warned border was border.caution.default; warned focus ring was the blue focus outline. */
          --cnvs-brand-common-caution-inner: var(--cnvs-sys-color-border-caution-default);
          --cnvs-brand-common-caution-outer: var(--cnvs-brand-common-focus-outline);
        }

        /**
         * Layer 2: legacy VNDLY badge colors
         *
         * Badge variants now point at Canvas v4 status-indicator tokens directly
         * (\`surface-*\`, \`fg-*\`). In legacy mode we need them to render in the
         * pre-rebrand VNDLY palette. Override the specific tokens Badge consumes
         * with their legacy values.
         *
         * Overrides \`--cnvs-sys-color-fg-muted-strong\` from Layer 1 (slate-700)
         * to match the original \`incomplete\` badge text color (gray.500).
         *
         * Not polyfilled:
         *   - \`info\` tokens — no legacy badge uses \`info\`; variant flips that
         *     introduce it are flag-gated at the callsite.
         *   - \`fg-inverse\` — already white in both modes.
         */
        :root {
          --cnvs-sys-color-surface-danger-strong: ${vndlyColors.red[100]};
          --cnvs-sys-color-fg-danger-strong: ${vndlyColors.red[700]};
          --cnvs-sys-color-surface-warning-strong: ${vndlyColors.yellow[200]};
          --cnvs-sys-color-fg-warning-strong: ${vndlyColors.red[700]};
          --cnvs-sys-color-surface-success-strong: ${vndlyBrand[100]};
          --cnvs-sys-color-fg-success-strong: ${vndlyBrand[700]};
          --cnvs-sys-color-surface-alt-strong: ${vndlyColors.gray[200]};
          --cnvs-sys-color-fg-muted-strong: ${vndlyColors.gray[500]};
          --cnvs-sys-color-accent-muted-default: ${vndlyColors.gray[500]};
        }

        /* THEMING VARIABLE DEFINITIONS */
        /**
        Legacy VNDLY WORKDAY Theme (frozen v3 snapshot)
        These are the global design tokens used throughout the application. They are aligned with the
        tokens defined in the design system.

        Try to use these tokens where possible.
        **/

        :root {
          /* Colors */
          /* Navigation Menu */
          --vndly-color-navigation-menu-bar-background: var(--cnvs-sys-color-bg-default);

          /* Feedback includes alerts (banners, toasts and modals), tooltips, badges */
          --vndly-color-feedback-icon-info: var(--cnvs-brand-primary-base);
          --vndly-color-feedback-background-info: var(--cnvs-base-palette-blue-100);
          --vndly-color-feedback-success: ${vndlyBrand[500]};
          --vndly-color-feedback-success-strong: ${vndlyBrand[700]};
          --vndly-color-feedback-icon-success: ${vndlyBrand[600]};
          --vndly-color-feedback-background-success: ${vndlyBrand[100]};
          --vndly-color-feedback-warning: ${vndlyColors.red[700]};
          --vndly-color-feedback-icon-warning: ${vndlyColors.red[700]};
          --vndly-color-feedback-background-warning: ${vndlyColors.yellow[200]};
          --vndly-color-feedback-danger: ${vndlyColors.red[600]};
          --vndly-color-feedback-danger-strong: ${vndlyColors.red[700]};
          --vndly-color-feedback-icon-danger: ${vndlyColors.red[600]};
          --vndly-color-feedback-background-danger: ${vndlyColors.red[100]};

          /* Button includes action buttons with either a primary, secondary or delete variant */
          --vndly-color-button: var(--cnvs-sys-color-fg-inverse);
          --vndly-color-button-background-primary: var(--cnvs-brand-primary-base);
          --vndly-color-button-background-primary-hover: var(--cnvs-brand-primary-dark);
          --vndly-color-button-background-primary-active: var(--cnvs-brand-primary-darkest);
          --vndly-color-button-background-primary-hover-disabled: var(--cnvs-brand-primary-base);
          --vndly-color-button-secondary: var(--cnvs-sys-color-fg-strong);
          --vndly-color-button-background-secondary: var(--cnvs-sys-color-bg-default);
          --vndly-color-button-border-secondary: var(--cnvs-sys-color-border-input-default);
          --vndly-color-button-secondary-hover: var(--cnvs-sys-color-fg-stronger);
          --vndly-color-button-background-secondary-hover: var(--cnvs-sys-color-bg-alt-default);
          --vndly-color-button-border-secondary-hover: var(--cnvs-sys-color-border-input-strong);
          --vndly-color-button-background-secondary-active: var(--cnvs-sys-color-bg-alt-strong);
          --vndly-color-button-tertiary: var(--cnvs-brand-primary-base);
          --vndly-color-button-tertiary-hover: var(--cnvs-brand-primary-dark);
          --vndly-color-button-tertiary-background: var(--cnvs-sys-color-bg-transparent-default);
          --vndly-color-button-tertiary-background-hover: var(--cnvs-sys-color-bg-alt-soft);
          --vndly-color-button-tertiary-background-active: var(--cnvs-sys-color-bg-alt-default);
          --vndly-color-button-background-delete-primary: var(--cnvs-brand-error-base);
          --vndly-color-button-background-delete-primary-hover: var(--cnvs-brand-error-dark);
          --vndly-color-button-background-delete-primary-active: var(--cnvs-brand-error-darkest);
          --vndly-color-button-background-delete-primary-disabled: var(--cnvs-brand-error-base);
          --vndly-color-button-delete-secondary: var(--cnvs-brand-error-base);
          --vndly-color-button-delete-secondary-hover: var(--cnvs-sys-color-fg-inverse);
          --vndly-color-button-delete-secondary-hover-disabled: var(--cnvs-brand-error-base);
          --vndly-color-button-background-delete-secondary: var(--cnvs-sys-color-bg-default);
          --vndly-color-button-background-delete-secondary-hover: var(--cnvs-brand-error-dark);
          --vndly-color-button-background-delete-secondary-active: var(--cnvs-brand-error-darkest);
          --vndly-color-button-border-delete-secondary: var(--cnvs-sys-color-border-contrast-strong);
          --vndly-color-button-border-delete-secondary-hover: var(--cnvs-brand-error-dark);

          /* Link includes button or inline links */
          --vndly-color-link: var(--cnvs-sys-color-text-primary-default);
          --vndly-color-link-hover: var(--cnvs-sys-color-text-primary-strong);
          --vndly-color-link-background: var(--cnvs-sys-color-bg-transparent-default);
          --vndly-color-link-background-hover: var(--cnvs-sys-color-bg-alt-soft);
          --vndly-color-link-inline-light: var(--cnvs-sys-color-fg-muted-default);
          /* Resolved directly to slate-700 so the Badge polyfill below can override
             --cnvs-sys-color-fg-muted-strong (incomplete badge text) without
             dragging this link-hover color along. */
          --vndly-color-link-inline-light-hover: var(--cnvs-base-palette-slate-700);
          --vndly-color-link-inline-dark: ${vndlyColors.gray[800]}; // DEPRECATED
          --vndly-color-link-inline-dark-hover: ${vndlyColors.gray[900]}; // DEPRECATED
          --vndly-color-link-delete: var(--cnvs-brand-error-base);
          --vndly-color-link-delete-hover: var(--cnvs-brand-error-darkest);

          /* Tab includes nav and filter tabs */
          --vndly-color-tab-nav-text: var(--cnvs-sys-color-fg-muted-default);
          --vndly-color-tab-nav-text-hover: var(--cnvs-sys-color-fg-muted-stronger);
          --vndly-color-tab-nav-text-selected: var(--cnvs-sys-color-fg-primary-default);
          --vndly-color-tab-nav-background: transparent;
          --vndly-color-tab-nav-background-hover: var(--cnvs-sys-color-bg-alt-default);
          --vndly-color-tab-nav-border-hover: transparent;
          --vndly-color-tab-nav-border-selected: var(--cnvs-brand-primary-base);
          --vndly-color-tab-filter-text: ${vndlyColors.gray[500]};
          --vndly-color-tab-filter-background-hover: ${vndlyColors.gray[200]};
          --vndly-color-tab-filter-background-checked: ${vndlyColors.gray[200]};
          --vndly-color-tablist-border: ${vndlyColors.gray[300]};

          /* Input, Select */
          --vndly-color-input-text: var(--cnvs-sys-color-text-default);
          --vndly-color-input-text-disabled: var(--cnvs-sys-color-fg-disabled);
          --vndly-color-input-text-hint: var(--cnvs-sys-color-text-hint);
          --vndly-color-input-text-label: var(--cnvs-sys-color-text-default);
          --vndly-color-input-text-required: var(--cnvs-sys-color-text-critical-default);
          --vndly-color-input-text-placeholder: var(--cnvs-sys-color-text-hint);
          --vndly-color-input-background: var(--cnvs-sys-color-bg-default);
          --vndly-color-input-background-disabled: var(--cnvs-sys-color-bg-alt-softer);
          --vndly-color-input-border: var(--cnvs-sys-color-border-input-default);
          --vndly-color-input-border-hover: var(--cnvs-sys-color-border-input-strong);
          --vndly-color-input-border-focused: var(--cnvs-brand-common-focus-outline);
          --vndly-color-input-border-invalid: var(--cnvs-sys-color-border-critical-default);
          --vndly-color-input-border-warned: var(--cnvs-sys-color-border-caution-default);
          --vndly-color-input-border-disabled: var(--cnvs-sys-color-border-input-disabled);
          --vndly-color-input-shadow-focused: var(--cnvs-brand-common-focus-outline);
          --vndly-color-input-shadow-invalid: var(--cnvs-sys-color-border-critical-default);
          --vndly-color-input-shadow-warned: var(--cnvs-sys-color-border-caution-default);
          --vndly-color-input-addon-border: var(--cnvs-sys-color-border-container);

          /* Checkbox, Radio */
          --vndly-color-input-icon: var(--cnvs-sys-color-icon-inverse);
          --vndly-color-input-icon-checked: var(--cnvs-sys-color-icon-inverse);
          --vndly-color-input-icon-indeterminate: var(--cnvs-sys-color-icon-inverse);
          --vndly-color-input-shadow-hover: var(--cnvs-sys-color-bg-alt-soft);
          --vndly-color-input-background-checked: var(--cnvs-brand-primary-base);
          --vndly-color-input-background-checked-disabled: var(--cnvs-brand-primary-light);
          --vndly-color-input-background-checked-invalid: var(--cnvs-brand-error-base);
          --vndly-color-input-background-checked-warned: var(--cnvs-brand-alert-base);
          --vndly-color-input-background-indeterminate: var(--cnvs-brand-primary-base);
          --vndly-color-input-background-indeterminate-disabled: var(--cnvs-brand-primary-light);
          --vndly-color-input-border-active: var(--cnvs-sys-color-border-input-strong);
          --vndly-color-input-border-checked: var(--cnvs-brand-primary-base);
          --vndly-color-input-border-checked-disabled: var(--cnvs-brand-primary-light);
          --vndly-color-input-border-indeterminate: var(--cnvs-brand-primary-base);
          --vndly-color-input-border-indeterminate-disabled: var(--cnvs-brand-primary-light);

          --vndly-color-listitem-background: transparent;
          --vndly-color-listitem-background-hover: var(--cnvs-base-palette-neutral-100);
          --vndly-color-listitem-background-selected: var(--cnvs-base-palette-blue-100);
          --vndly-color-listitem-background-selected-hover: var(--cnvs-base-palette-blue-100);
          --vndly-color-listitem-border: var(--cnvs-sys-color-bg-alt-default);
          --vndly-color-listitem-selected-indicator-background: var(--cnvs-brand-primary-base);

          /* Container includes cards, drawers, popovers, modals and any general container properties such as backgrounds and text */
          --vndly-color-container-light: white;
          --vndly-color-container-dark: ${vndlyColors.gray[800]};
          --vndly-color-container-subdued: ${vndlyColors.gray[500]};
          --vndly-color-container-background-light: white;
          --vndly-color-container-background-dark: ${vndlyColors.gray[800]};
          --vndly-color-container-background-subdued: ${vndlyColors.gray[100]};
          --vndly-color-app-background: ${vndlyColors.gray[100]};
          --vndly-color-container-border: ${vndlyColors.gray[300]};
          --vndly-color-container-border-focused: var(--cnvs-brand-common-focus-outline);
          --vndly-color-container-icon: ${vndlyColors.gray[600]};
          --vndly-color-container-icon-hover: ${vndlyColors.gray[900]};
          --vndly-color-container-icon-info: var(--cnvs-sys-color-icon-info-default);
          --vndly-color-container-icon-danger: var(--cnvs-sys-color-icon-critical-default);

          /* Typography */
          --vndly-font-family: Roboto, system-ui, sans-serif;
          --vndly-font-family-mono: 'Roboto Mono', 'Courier New', Courier, monospace;

          --vndly-font-weight-light: 300;
          --vndly-font-weight-normal: 400;
          --vndly-font-weight-semibold: 500;
          --vndly-font-weight-bold: 700;

          --vndly-font-size-sm: 0.875rem;
          --vndly-font-size-md: 1rem;
          --vndly-font-size-lg: 1.125rem;
          --vndly-font-size-xl: 1.25rem;
          --vndly-font-size-2xl: 1.5rem;
          --vndly-font-size-3xl: 1.875rem;
          --vndly-font-size-4xl: 2.25rem;

          --vndly-font-line-height-none: 1;
          --vndly-font-line-height-sm: 1.25;
          --vndly-font-line-height-md: 1.5; /* base */
        }

        /**
         * Layer 3: canvas-only style polyfills for flag-OFF.
         *
         * Card consumers now use Canvas Outlined style. Each block restores a
         * legacy decoration (shadow, hover-lift, transition) that used to live
         * inline in a component. Scoped to a data-attribute so flag-ON stays
         * flat. Flat card shadows are not here. Those come from
         * legacyCardLayerStyles. Layer 3 is the stuff that is not a single token.
         */

        /* PublishCard idle shadow + hover-lift. Was inline sx on PublishCard.tsx. */
        [data-publish-card] {
          box-shadow:
            0 1px 2px 0 rgba(0, 0, 0, 0.1),
            0 1px 1px 0 rgba(0, 0, 0, 0.06);
          transform: scale3d(1, 1, 1);
          will-change: transform, box-shadow;
          transition-property: box-shadow, transform;
          transition-duration: 0.35s;
          transition-timing-function: ease-in-out;
        }
        [data-publish-card]:hover {
          transform: scale3d(1.01, 1.01, 1.01);
          box-shadow:
            0px 15px 16px 4px rgb(0 0 0 / 10%),
            -1px 9px 3px 3px rgb(0 0 0 / 3%);
        }

        /* Avatar circle shadow. Was boxShadow="md" on PublishCard Circle. */
        [data-publish-card] [data-avatar-circle] {
          box-shadow: var(--chakra-shadows-md);
        }

        /* ResultItem grid hover-lift. Was inline in ResultItem.tsx gridStyles. */
        [data-items-list-display-mode='grid'] [data-items-list-item]:hover {
          transform: scale3d(1.01, 1.01, 1.01);
          box-shadow:
            0px 15px 16px 4px rgb(0 0 0 / 10%),
            -1px 9px 3px 3px rgb(0 0 0 / 3%);
        }

        /* Square header/footer corners for slots that skip Card theme parts. */
        [data-card] > header[data-card-header] {
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }
        [data-card] > footer[data-card-footer] {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        /* No-spacing results content shadow. Was MainNoSpacing sx. */
        [data--no-spacing] [data-results-layout-main-content]:has([data-result-items]),
        [data--no-spacing] [data-results-layout-main-content] [data-empty-state-container] {
          box-shadow:
            0 1px 2px 0 rgba(0, 0, 0, 0.1),
            0 1px 1px 0 rgba(0, 0, 0, 0.06);
        }
      `}
    />
  )
}
